import {useMemo} from 'react';
import {TimeBasedColors, TimePeriod, getTimePeriod} from '../types';

/**
 * Color schemes for different times of day
 */
const COLOR_SCHEMES: Record<TimePeriod, TimeBasedColors> = {
  morning: {
    // Warm sunrise colors
    primary: '#ff9e80',
    text: '#fff8f0',
    textMuted: 'rgba(255,248,240,0.7)',
    background: 'rgba(45,35,28,0.9)',
    gradient: ['rgba(45,35,28,0.9)', 'transparent'],
    greeting: '#ffd4c4',
    surface: 'rgba(45,35,28,0.85)',
    border: 'rgba(255,158,128,0.2)',
  },
  afternoon: {
    // Bright daylight
    primary: '#ffd54f',
    text: '#ffffff',
    textMuted: 'rgba(255,255,255,0.7)',
    background: 'rgba(30,30,35,0.85)',
    gradient: ['rgba(30,30,35,0.85)', 'transparent'],
    greeting: '#fff3e0',
    surface: 'rgba(30,30,35,0.8)',
    border: 'rgba(255,213,79,0.2)',
  },
  evening: {
    // Warm sunset
    primary: '#ff7043',
    text: '#ffe4d6',
    textMuted: 'rgba(255,228,214,0.7)',
    background: 'rgba(40,25,20,0.92)',
    gradient: ['rgba(40,25,20,0.92)', 'transparent'],
    greeting: '#ffccbc',
    surface: 'rgba(40,25,20,0.88)',
    border: 'rgba(255,112,67,0.2)',
  },
  night: {
    // Calm dark
    primary: '#90caf9',
    text: '#e3f2fd',
    textMuted: 'rgba(227,242,253,0.6)',
    background: 'rgba(20,25,35,0.95)',
    gradient: ['rgba(20,25,35,0.95)', 'transparent'],
    greeting: '#bbdefb',
    surface: 'rgba(20,25,35,0.9)',
    border: 'rgba(144,202,249,0.2)',
  },
};

/**
 * Hook that returns colors based on time of day
 * @param currentTime - The current time to base colors on
 * @returns TimeBasedColors for the current time period
 */
export function useTimeBasedColors(currentTime: Date): TimeBasedColors {
  return useMemo(() => {
    const period = getTimePeriod(currentTime);
    return COLOR_SCHEMES[period];
  }, [currentTime]);
}

/**
 * Get colors for a specific time period (non-hook version for static contexts)
 */
export function getTimeBasedColors(currentTime: Date): TimeBasedColors {
  const period = getTimePeriod(currentTime);
  return COLOR_SCHEMES[period];
}

/**
 * Get the current time period name (for debugging/display)
 */
export function getTimePeriodName(currentTime: Date): TimePeriod {
  return getTimePeriod(currentTime);
}

export default useTimeBasedColors;
